import type { ReactNode } from 'react';
import type { FeatureKey, PermissionCode } from '@stockpilot/shared';
import { ShieldOff } from 'lucide-react';
import { useAccess } from '@/hooks/useAccess';
import { EmptyState } from '@/components/ui';

/**
 * Route-level guard. It mirrors the API's decision so the user gets a clear
 * explanation instead of a wall of failed requests — the API is still the one
 * that actually refuses.
 */
export function RequirePermission({
  permissions,
  feature,
  children,
}: {
  permissions?: PermissionCode[];
  feature?: FeatureKey;
  children: ReactNode;
}) {
  const { canAny, hasFeature } = useAccess();

  if (feature && !hasFeature(feature)) {
    return (
      <EmptyState
        icon={ShieldOff}
        title="This module is not enabled"
        description="Your current plan does not include this module. An owner can enable it from Subscription."
      />
    );
  }

  if (permissions && permissions.length > 0 && !canAny(...permissions)) {
    return (
      <EmptyState
        icon={ShieldOff}
        title="You do not have access to this page"
        description="Ask an administrator to grant you the required permission."
      />
    );
  }

  return <>{children}</>;
}
